import {
    Box,
    IconButton,
    InputBaseComponentProps,
    TextField,
    Tooltip,
} from '@mui/material';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

interface NumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    step?: number;
    min?: number;
    max?: number;
    precision?: number;
    placeholder?: string;
    disabled?: boolean;
    inputProps?: InputBaseComponentProps;
    fullWidth?: boolean;
    label?: ReactNode;
    autoFocus?: boolean;
    error?: string | null;
    'data-testid'?: string;
}

const roundToPrecision = (num: number, precision: number = 10) => {
    const factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
};

const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    step = 1,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    precision = 2,
    placeholder = '',
    disabled = false,
    inputProps,
    label,
    fullWidth = false,
    autoFocus = false,
    error,
    'data-testid': dataTestId,
}) => {
    const intl = useIntl();
    const [internalValue, setInternalValue] = useState<string>('');
    const [isTouched, setIsTouched] = useState(false);

    // Reset editing state when the external value changes
    // (e.g., parent resets the form)
    useEffect(() => {
        setIsTouched(false);
    }, [value]);

    // Show prop value when not editing, internal state when editing
    const displayValue = isTouched
        ? internalValue
        : value === null || value === undefined
          ? ''
          : value.toFixed(precision);

    const validateValue = (val: string): boolean => {
        const num = parseFloat(val);
        return !isNaN(num) && num >= min && num <= max;
    };

    const handleFocus = () => {
        // Seed the editing buffer from the current formatted value so the user
        // starts editing from the correct number, not a stale initialisation.
        setInternalValue(displayValue);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        if (
            newValue === '' ||
            new RegExp(`^-?\\d*\\.?\\d{0,${precision}}$`).test(newValue)
        ) {
            setInternalValue(newValue);
            setIsTouched(true);
        }
    };

    const handleIncrement = () => {
        if (disabled) {
            return;
        }
        const newValue =
            value !== null
                ? Math.min(roundToPrecision(value + step, precision), max)
                : roundToPrecision(step, precision);
        setInternalValue(newValue.toFixed(precision));
        onChange(newValue);
        setIsTouched(false);
    };

    const handleDecrement = () => {
        if (disabled) {
            return;
        }
        const newValue =
            value !== null
                ? Math.max(roundToPrecision(value - step, precision), min)
                : roundToPrecision(-step, precision);
        setInternalValue(newValue.toFixed(precision));
        onChange(newValue);
        setIsTouched(false);
    };

    const handleBlur = () => {
        if (!isTouched) {
            return;
        }

        const numericValue = parseFloat(internalValue);
        if (validateValue(internalValue)) {
            const clampedValue = Math.max(
                Math.min(roundToPrecision(numericValue, precision), max),
                min
            );
            onChange(clampedValue);
        }
        // Always exit editing mode; the display reverts to the (updated) prop.
        setIsTouched(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            handleBlur();
        }
    };

    const tooltipContent = () => {
        const parts: string[] = [];
        if (min > Number.NEGATIVE_INFINITY) {
            parts.push(`Min: ${min}`);
        }
        if (max < Number.POSITIVE_INFINITY) {
            parts.push(`Max: ${max}`);
        }
        return parts.join(', ');
    };

    return (
        <Tooltip
            open={isTouched}
            placement="bottom-start"
            title={tooltipContent() || ''}
            disableHoverListener={!tooltipContent()}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -12],
                        },
                    },
                ],
            }}
        >
            <Box
                position="relative"
                display="flex"
                alignItems="center"
                data-testid={dataTestId}
            >
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth={fullWidth}
                    value={displayValue}
                    label={label}
                    onFocus={handleFocus}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    error={Boolean(
                        error || (!validateValue(internalValue) && isTouched)
                    )}
                    inputProps={{
                        inputMode: 'decimal',
                        ...inputProps,
                    }}
                />
                <Box display="grid" position="absolute" right={8}>
                    <IconButton
                        size="small"
                        onClick={handleIncrement}
                        disabled={disabled}
                        aria-label={intl.formatMessage({ id: 'increaseValue' })}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9650;
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleDecrement}
                        disabled={disabled}
                        aria-label={intl.formatMessage({ id: 'decreaseValue' })}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9660;
                    </IconButton>
                </Box>
            </Box>
        </Tooltip>
    );
};

export default NumberInput;
